import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { deleteConfirmPhrase } from "@/utils/provisionErrors";

type DeleteSiteModalProps = {
  open: boolean;
  domain: string;
  busy?: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmText: string;
  onConfirmTextChange: (value: string) => void;
};

export default function DeleteSiteModal({
  open,
  domain,
  busy = false,
  error = "",
  onClose,
  onConfirm,
  confirmText,
  onConfirmTextChange
}: DeleteSiteModalProps) {
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const expected = deleteConfirmPhrase(domain);
  const trimmed = confirmText.trim().toLowerCase();
  const canDelete = trimmed === expected && !busy;
  const isPartialMatch = trimmed.length > 0 && expected.startsWith(trimmed);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, busy, onClose]);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      className="modalBackdrop deleteSiteBackdrop open"
      aria-hidden="false"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="modal deleteSiteModal" role="dialog" aria-modal="true" aria-label="Delete site">
        <div className="deleteSiteModalHead">
          <div className="deleteSiteModalIcon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="deleteSiteModalHeadText">
            <div className="modalTitle">Delete site</div>
            <div className="modalSub">
              Permanently remove <span className="mono">{domain}</span> and all associated data
            </div>
          </div>
          <button type="button" className="iconBtn deleteSiteModalClose" title="Close" disabled={busy} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="modalBody deleteSiteModalBody">
          <div className="deleteSiteWarn">
            <strong>This action cannot be undone</strong>
            <p>
              Settings, logs, firewall rules, cached pages, and all tenant data for{" "}
              <span className="mono">{domain}</span> will be permanently deleted.
            </p>
          </div>

          <div className="deleteSiteConfirmBlock">
            <label className="deleteSiteConfirmLabel" htmlFor="deleteSiteConfirmInput">
              Type the following to confirm
            </label>
            <div className="deleteSiteConfirmPhrase mono">{expected}</div>
            <input
              id="deleteSiteConfirmInput"
              ref={inputRef}
              type="text"
              className={
                "deleteSiteConfirmInput settingsInput settingsMono"
                + (canDelete ? " is-match" : isPartialMatch ? " is-partial" : "")
              }
              autoComplete="off"
              spellCheck={false}
              value={confirmText}
              disabled={busy}
              placeholder={expected}
              onChange={(e) => onConfirmTextChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && canDelete) onConfirm(); }}
            />
          </div>

          {error ? <p className="err deleteSiteErr">{error}</p> : null}
        </div>

        <div className="modalActions deleteSiteModalActions">
          <button type="button" className="btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={"btn danger deleteSiteSubmit" + (canDelete ? " is-ready" : "")}
            disabled={!canDelete}
            onClick={onConfirm}
          >
            {busy ? "Deleting…" : "Delete site"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
